import { usePage } from '@inertiajs/react';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Toaster } from '@/components/ui/Sonner';
import { GLOBALLY_HANDLED_VALIDATION_ERROR_KEYS } from '@/constants/validationErrorKeys';
import { PageProps } from '@/types';

const globallyHandledValidationErrorKeys = new Set<string>(GLOBALLY_HANDLED_VALIDATION_ERROR_KEYS);

interface FlashMessagesProps {
  richColors?: boolean;
}

export function FlashMessages({ richColors = false }: FlashMessagesProps) {
  const page = usePage<PageProps>();
  const { session, errors } = page.props;

  useEffect(() => {
    if (session?.success) {
      toast.success(session.success, {
        id: `session-success:${session.nonce}`
      });
    }

    if (session?.error) {
      toast.error(session.error, {
        id: `session-error:${session.nonce}`
      });
    }

    if (session?.warning) {
      toast.warning(session.warning, {
        id: `session-warning:${session.nonce}`
      });
    }

    if (session?.info) {
      toast.info(session.info, {
        id: `session-info:${session.nonce}`
      });
    }

    // Filter out errors handled by a dedicated global surface (a modal). A key can hold a list of
    // messages, so count the messages rather than the keys.
    const filteredErrors = Object.entries(errors).flatMap(([key, value]) =>
      globallyHandledValidationErrorKeys.has(key) ? [] : Array.isArray(value) ? value : [value]
    );

    if (filteredErrors.length === 0) {
      return;
    }

    const errorToDisplay =
      filteredErrors.length > 1 ? 'There are some errors with your submission.' : filteredErrors[0];

    if (errorToDisplay) {
      toast.error(errorToDisplay, {
        id: `validation-error:${session.nonce}`
      });
    }
  }, [session, errors]);

  return <Toaster position="bottom-right" closeButton richColors={richColors} />;
}
